import type { HTMLAttributes } from "react";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { Account } from "../../../../../shared/domains/account/account";
import { getAvailableBalance } from "../../../../../shared/domains/account/account";
import { Path } from "../../../../core/routing/path";
import { AmountText } from "../../../common/amount-text";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { DotsIcon } from "../../../common/svg/dots-icon";
import { theme } from "../../../styles/theme";

interface AccountBalanceCardProps extends HTMLAttributes<HTMLDivElement> {
  account: Account;
  onUpdate?: () => void;
}

export function AccountBalanceCard(props: Readonly<AccountBalanceCardProps>) {
  const { account, onUpdate, ...rest } = props;
  const { formatMessage } = useIntl();
  const { push } = useHistory();

  return (
    <Card {...rest}>
      <TopRow>
        <BalanceLabel>{formatMessage("homeScreen.accountBalance")}</BalanceLabel>
        <AccountNumber>{formatMessage("account.accountId", { id: account.id })}</AccountNumber>
      </TopRow>
      <BottomRow>
        <Balance amount={getAvailableBalance(account)} />
        <DetailsButton aria-label={formatMessage("myAccountScreen.title")} onClick={() => push(Path.MyAccount)}>
          <WhiteDotsIcon />
        </DetailsButton>
      </BottomRow>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: stretch;
  min-height: 118px;
  padding: 20px 24px;
  border-radius: 16px;
  background: ${theme.brandGradient};
  color: #ffffff;
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const BottomRow = styled(TopRow)`
  align-items: flex-end;
`;

const BalanceLabel = styled.span`
  ${theme.mediumText};
  font-size: 14px;
  line-height: 18px;
  opacity: 0.85;
`;

const AccountNumber = styled.span`
  ${theme.mediumText};
  font-size: 12px;
  line-height: 16px;
  opacity: 0.7;
`;

const Balance = styled(AmountText)`
  ${theme.boldText};
  font-size: 1.75rem;
  line-height: 34px;
`;

const DetailsButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const WhiteDotsIcon = styled(DotsIcon)`
  fill: #ffffff;
  width: 20px;
  height: 20px;
`;
